import Config from './Config.js';

/**
 * Launcher — the 1mind launcher script itself: build the URL, inject it
 * once, and report when the experience has mounted.
 *
 * 1mind always mounts into an element with the id in Config.mountId. That
 * element is also what decides EMBED vs WIDGET: verified 2026-08-28, when it
 * exists the launcher renders inline into it even for a widget deployment
 * with data-display_mode="widget" set. So creating the mount before
 * injecting the script is what pins the embedded experience in place.
 */

var Launcher = new Object();

/**
 * The injected script element — also the run-once guard. One launcher per
 * page: a second would open a second 1mind session.
 */
Launcher.script = null;

/**
 * Poll handle for the mount watcher.
 */
Launcher.timer = null;

/**
 * Resolve a launcher URL for a use case / environment pair.
 *
 * @param  {string} useCase  embedded | widget | behindTheForm
 * @param  {string} env      sandbox | production
 * @return {string}
 */
Launcher.url = function (useCase, env) {

  var deployment = Config.deployments[useCase];

  if (!deployment) {

    throw new Error('[1mind] unknown use case "' + useCase + '"');

  }

  if (env !== 'sandbox' && env !== 'production') {

    throw new Error('[1mind] env must be "sandbox" or "production", got "' + env + '"');

  }

  return Config.launcherBase + deployment[env];

};

/**
 * Is 1mind already running on this page? True once any code — this bundle,
 * the connect bar's AI Advisor panel, or a hand-written section — has
 * injected the launcher or created the mount.
 *
 * 1mind mounts into a single fixed element id, so a page can only host ONE
 * experience. This is a DOM check on purpose: each bundle carries its own
 * copy of this module, so module-level state cannot see across them.
 *
 * @return {boolean}
 */
Launcher.claimed = function () {

  if (document.querySelector('script[src^="' + Config.launcherBase + '"]')) return true;

  return !!document.getElementById(Config.mountId);

};

/**
 * Ensure the mount element exists inside the caller's container. 1mind owns
 * the id, so the container is whatever the page provides and the mount is
 * created inside it.
 *
 * @param  {string|Element} container  Selector or element
 * @return {Element|null}   The mount element, or null when the container
 *                          isn't on the page
 */
Launcher.mount = function (container) {

  var host = typeof container === 'string' ? document.querySelector(container) : container;

  if (!host) return null;

  var existing = document.getElementById(Config.mountId);

  if (existing) return existing;

  var mount = document.createElement('div');

  mount.id = Config.mountId;
  mount.className = 'onemind-mount';

  host.appendChild(mount);

  return mount;

};

/**
 * Inject the launcher script. Idempotent — later calls return the element
 * injected by the first.
 *
 * @param  {object} options  { useCase, env, attributes }
 * @return {Element}
 */
Launcher.inject = function (options) {

  var _this = this;

  if (_this.script) return _this.script;

  // Something else already loaded a launcher (the connect bar's AI Advisor
  // panel, or another section on the page) — adopt it rather than opening a
  // second 1mind session.
  var existing = document.querySelector('script[src^="' + Config.launcherBase + '"]');

  if (existing) {

    _this.script = existing;

    return existing;

  }

  var script = document.createElement('script');

  script.defer = true;
  script.src = _this.url(options.useCase, options.env);

  var attributes = options.attributes || {};

  Object.keys(attributes).forEach(function (key) {

    // Values reach 1mind as strings; skip empties so an absent form field
    // doesn't overwrite context 1mind already has from the HubSpot cookie.
    if (attributes[key] === null || attributes[key] === undefined || attributes[key] === '') return;

    script.setAttribute(key, String(attributes[key]));

  });

  document.head.appendChild(script);

  _this.script = script;

  return script;

};

/**
 * Wait for the experience to mount. The launcher fetches the deployment
 * before rendering, so there is no load event to hang a spinner on.
 *
 * @param  {function} onReady        Called with the mount element
 * @param  {function} [onUnavailable] Called when it never mounts
 */
Launcher.whenReady = function (onReady, onUnavailable) {

  var _this = this;

  clearInterval(_this.timer);

  var attempts = 0;

  _this.timer = setInterval(function () {

    var mount = document.getElementById(Config.mountId);

    if (mount && mount.querySelector('iframe')) {

      clearInterval(_this.timer);

      if (typeof onReady === 'function') onReady(mount);

      return;

    }

    if (++attempts >= 50) { // ~15s

      clearInterval(_this.timer);

      if (typeof onUnavailable === 'function') onUnavailable();

    }

  }, 300);

};

export default Launcher;
